import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";
import GlossaryText from "./GlossaryText";

export default function AIInsight({ symbol, name, asset_type, price, change_pct, range, points = [] }) {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState("");

  const fetchExplanation = async () => {
    setLoading(true);
    try {
      const summary_points = points.slice(-12).map((p) => p.c).filter((v) => v != null);
      const res = await api.post("/ai/explain", {
        symbol, name, asset_type, price, change_pct,
        timeframe: range, summary_points,
      });
      setExplanation(res.data.explanation);
    } catch (e) {
      setExplanation("Couldn't fetch the explanation right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-white/85 backdrop-blur-xl border border-white/60 p-6 relative overflow-hidden"
      style={{ boxShadow: "0 0 0 1px rgba(209,142,99,0.18), 0 12px 40px rgba(42,47,43,0.06)" }}
      data-testid="ai-insight-panel"
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D18E63] to-[#547A8F]" />
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D18E63] to-[#547A8F] flex items-center justify-center animate-pulse-glow">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              UpTick AI
            </div>
            <div className="font-heading text-lg text-[#2A2F2B]">
              Beginner-friendly breakdown
            </div>
          </div>
        </div>
      </div>

      {!explanation && !loading && (
        <div className="space-y-3">
          <p className="text-sm text-[#6B7068] leading-relaxed">
            Get a plain-English explanation of what this chart is showing — perfect if you're
            new to trading and want to understand the price action without the jargon.
          </p>
          <Button
            onClick={fetchExplanation}
            data-testid="ai-explain-btn"
            className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm"
          >
            <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Explain this chart
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-3 text-sm text-[#6B7068] py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          UpTick is reading the chart...
        </div>
      )}

      {explanation && !loading && (
        <div data-testid="ai-explanation-text" className="space-y-3 animate-fade-up">
          <GlossaryText className="text-[15px] leading-relaxed text-[#2A2F2B] block">
            {explanation}
          </GlossaryText>
          <Button
            variant="ghost"
            onClick={fetchExplanation}
            data-testid="ai-regenerate-btn"
            className="rounded-full text-xs text-[#547A8F] hover:bg-[#547A8F]/10 px-3"
          >
            Regenerate
          </Button>
        </div>
      )}
    </div>
  );
}
